import { Injectable, Inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { DashboardDataCache } from './dashboard-data.cache';
import { DashboardData, LoanFacts, BorrowerIdentifyingInfo } from '../../models';
import { SessionService } from '../session/shared/session/session-service/session.service';
import LoanDashboardCategory = LoanFacts.LoanDashboardCategoryEnum;
import MortgageProviderTypeEnum = LoanFacts.MortgageProviderTypeEnum;

export class DashboardLoanCategory {
  public label: string;
  public loans: LoanFacts[];
}

@Injectable()
export class DashboardService {
  public uniqueLoanList: string[] = [];

  public get isMultiBorrowerAccount(): BehaviorSubject<boolean> {
    return this.sessionService.data.authData.isMultiBorrowerAccount;
  }

  public set dashboardSsnToFilterBy(value: string) {
    this.sessionService.data.authData.dashboardSsnToFilterBy = value;
  }

  public get dashboardSsnToFilterBy(): string {
    return this.sessionService.data.authData.dashboardSsnToFilterBy;
  }

  public set dashboardNameToFilterBy(value: string) {
    this.sessionService.data.authData.dashboardNameToFilterBy = value;
  }

  public get dashboardNameToFilterBy(): string {
    return this.sessionService.data.authData.dashboardNameToFilterBy;
  }

  public get dashboardBorrowerIdToFilterBy(): string {
    return this.sessionService.data.authData.dashboardBorrowerIdToFilterBy;
  }

  public get primaryBorrowerId(): string {
    return this.sessionService.mortgageSession.data.borrowerData.primaryBorrowerData.borrowerId;
  }

  constructor (
    private dashboardDataCache: DashboardDataCache,
    private sessionService: SessionService) {}

  public skipDashboardIfOnlyOneMortgageLoan(): Observable<boolean> {
    return this.getCategorizedLoanFacts(false)
      .map(loanCategories => {
        loanCategories = loanCategories.filter(loanCategory => loanCategory.loans.length);

        if (loanCategories.length === 1) {
          if (loanCategories[0].loans.length === 1) {
            const loan = loanCategories[0].loans[0];
            if (loan.loanDashboardCategory !== LoanDashboardCategory.Historic &&
              loan.loanDashboardCategory !== LoanDashboardCategory.Paying) {
                if (loan.mortgageProviderType !== MortgageProviderTypeEnum.PersonalLoans) {
                  return true;
                }
              }
            }
          }
          return false;
      });
  }

  getLoanFacts(refresh: boolean = true): Observable<DashboardData> {
    return this.dashboardDataCache.retrieve(refresh);
  }

  getCategorizedLoanFacts(refresh: boolean = true): Observable<DashboardLoanCategory[]> {
    return this.getLoanFacts(refresh)
      .map(data => this.categorizeLoanSet(data));
  }

  clearCache(): void {
    this.dashboardDataCache.clear();
  }

  // This should be the one place where we sort, filter, and categorize loans coming in to dashboard-related UI.
  // Doing all that here allows the header, side panel, and dashboard proper to all render loan info in the same
  // way, under the same category labels.  Any consumer of a dashboard GET loan set should be able to grab the
  // categorized loan set, render each category in the order they're returned from here, and be fine.
  private categorizeLoanSet(rawData: DashboardData): DashboardLoanCategory[] {
    const getLoansByCategory = (category: LoanDashboardCategory) => {

      let loans = {};
      let loanList = rawData.loans.filter(loan => loan.loanDashboardCategory === category);

      const filterBy = (predicate: (b: BorrowerIdentifyingInfo) => boolean) => {
        return loanList.filter(l => l.mortgageProviderType === MortgageProviderTypeEnum.PersonalLoans || l.borrowers.some(predicate));
      };

      if (this.dashboardSsnToFilterBy) {
        loanList = filterBy(b => b.code === this.dashboardSsnToFilterBy);
      } else if (this.dashboardNameToFilterBy) {
        loanList = filterBy(b => `${b.firstName} ${b.lastName}` === this.dashboardNameToFilterBy);
      }

      const isMultiBorrowerAccount: () => boolean = () => {

        const onlyDistinctBorrowers = (value, index, self) => {
          const name = (o) => `${o.firstName} ${o.lastName}`;
          return self.map(name).indexOf(name(value)) === index;
        };

        const borrowers = rawData.loans.length
          ? rawData.loans
            .map(l => l.borrowers || [])
            .reduce((a, b) => a.concat(b))
            .filter(onlyDistinctBorrowers)
          : [];

        const isMultiBorrower = borrowers.length > 1;
        this.isMultiBorrowerAccount.next(isMultiBorrower);

        return isMultiBorrower;
      };

      const uniqueLoanList = loanList.filter(loan => {
        if (loans[loan.displayLoanId]) {
          // LDTSULA-3729 API only returns duplicate loans when the primary borrower and coborrower share the same email
          // This is the criteria for being a multiborrower account.
          return false;
        } else {
          loans[loan.displayLoanId] = true;
          return true;
        }
      });

      return uniqueLoanList; // TODO: omit category if no loans are present in it
    };

    this.uniqueLoanList = getLoansByCategory(LoanDashboardCategory.Unsubmitted)
      .concat(getLoansByCategory(LoanDashboardCategory.PendingApproval))
      .concat(getLoansByCategory(LoanDashboardCategory.Paying))
      .concat(getLoansByCategory(LoanDashboardCategory.Historic))
      .map(loan => loan.loanID);

    return [
      {
        label: 'My Applications',
        loans: getLoansByCategory(LoanDashboardCategory.Unsubmitted)
          .concat(getLoansByCategory(LoanDashboardCategory.PendingApproval))
      },
      { label: 'My Loans', loans: getLoansByCategory(LoanDashboardCategory.Paying)},
      { label: 'My History', loans: getLoansByCategory(LoanDashboardCategory.Historic)}
    ];
  }
}
